import ContactNameFields from '../shared/ContactNameFields';
import ResponsiveTable from '../shared/ResponsiveTable';
import '../../styles/restScreens.css';
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { supabaseClient } from '../../config/supabase';
import AccountRow from './AccountRow';
import ClientHybridCard, { Pill, Photo } from './ClientHybridCard';
import ClientFullCard from './ClientFullCard';
import ProgressiveDisclosure from '../shared/ProgressiveDisclosure';
import useViewModePreference from '../shared/useViewModePreference';
import { clientDisclosureConfig } from './clientDisclosureConfig';
import {
    ACCOUNT_SELECT, ACCOUNT_STATUS_META, firstEmail, firstPhone,
    hasHumanReview, statusMeta, tierTone, toneTextStyle,
    VIEW_MODES, VIEW_STORAGE_KEY,
} from './clientsConstants';
import { SupportWriteGuard } from '../../context/SupportSessionContext';
import useModuleAccess from '../../hooks/useModuleAccess';
import HoverHelp from '../shared/HoverHelp';
import { formatPhone } from '../../lib/phone';
import LabeledMultiField from '../shared/LabeledMultiField';
import ContactImportPanel from './ContactImportPanel';
import useCanManageCompanyContacts from '../../hooks/useCanManageCompanyContacts';
import { addCompanyClient, accountNameLabel } from '../../lib/companyContactAdd';
import { buildExportCsv, downloadCsv } from '../../lib/contactCsv';
import { emptyEntry } from '../../lib/contactMethods';
import { CRM_TABS, createPersonalContact, fetchPersonalContactsStatus } from '../../lib/personalContacts';
import PersonalContactsPanel from './personal/PersonalContactsPanel';
import PersonalContactForm, { blankPersonalDraft } from './personal/PersonalContactForm';
import SaveToChoice from './personal/SaveToChoice';
import ContactTimeZoneField from './ContactTimeZoneField';
import useCurrentActivity from './useCurrentActivity';
import { EMPTY_ACTIVITY } from '../../lib/currentActivity';

import { BTN, TILE, TILE_INTERACTIVE, standardPillClass } from '../ui/platformStandard';
// Module 2 — the Clients surface (UX Addendum §2/§3).
//
// Three views, one toggle: List (scan speed) · Card (visual browse) · Hybrid (the middle
// layer). Default = each user's last-used view, remembered (Gate 3). Clicking any List row
// or Card tile opens the SAME Tier-2 card in place — one destination, three doors.
//
// BL-177 Step 2j: Card and Hybrid cards wear the fixed peacock strip on top. Hybrid shows exactly two cards per row (one on a phone);
// each is double width: the normal card on the left, "Current activity" (next trip / latest quote, last message, open items) on the
// right - loaded for every visible account in one call (useCurrentActivity), under the viewer's own conversation-privacy rules.
//
// Self-loading, like EngagementsView (the current house pattern): RLS scopes the rows, so
// this never filters by owner itself. What you see IS your book of business — enforced in
// the database, not here (blueprint decision #2).
//
// A row is an ACCOUNT wearing its primary contact's face (Val, 2026-07-15).
//
// COLOUR IS NAMED, NEVER CHOSEN. Every surface/ink here is a var() resolved by CSS, and the
// .dark class on <html> swaps them. That is why there is no `dark` flag threaded through this
// file any more: a component branching on the theme is a component picking a colour, and that
// habit is what produced 73 hardcoded hexes. Accent = brand-* (per entity, via the switcher);
// the gradient rule and semantics are fixed platform signatures. See global.css.

const COLUMNS = ['Contact', 'Position', 'Company', 'Account #', 'Tier', 'Status', 'Pipeline'];
const INVOICE_ACTIVITY_BATCH_SIZE = 100;

// Comms next pass (5e): every email on the record, lower-cased, whatever shape it is stored in (contacts.emails strings,
// { email | value | address } objects, or contact_methods rows of kind "email"). Contacts are stored like
// "VaLyn817+Test01@…", so matching must never depend on the capital letters.
export function contactEmailValues(contact) {
    const raw = [
        ...(Array.isArray(contact?.emails) ? contact.emails : []),
        ...(Array.isArray(contact?.contact_methods) ? contact.contact_methods.filter(m => m?.kind === 'email') : []),
    ];
    return [...new Set(raw
        .map(v => (typeof v === 'string' ? v : v?.email || v?.value || v?.address || ''))
        .map(v => String(v || '').trim().toLocaleLowerCase())
        .filter(Boolean))];
}

function contactPhoneValues(contact) {
    return [
        ...(contact?.phones || []),
        ...(Array.isArray(contact?.contact_methods) ? contact.contact_methods.filter(m => m?.kind === 'phone') : []),
    ].map(p => (typeof p === 'string' ? p : p?.number || p?.phone || p?.value || p?.phone_number)).filter(Boolean);
}

/** Case-insensitive contact search (name, position, emails, phones, tags); 3+ digits also match inside a number. */
export function contactMatchesCrmSearch(contact, search) {
    const q = String(search || '').trim().toLocaleLowerCase();
    if (!q) return true;
    const phoneValues = contactPhoneValues(contact);
    const digits = q.replace(/\D/g, '');
    if (digits.length >= 3 && phoneValues.some(p => p.replace(/\D/g, '').includes(digits))) return true;
    return [contact?.name, contact?.position, ...contactEmailValues(contact), ...phoneValues,
        ...phoneValues.map(p => formatPhone(p)), ...(contact?.tags || [])]
        .some(v => String(v || '').toLocaleLowerCase().includes(q));
}

/** CRM contacts with no account at all: no primary account and no account links (e.g. after their account was deleted). */
export function contactsWithoutAccount(contacts = []) {
    return contacts.filter(c => c && !c.merged_into_id && !c.primary_account_id
        && !(Array.isArray(c.contact_accounts) && c.contact_accounts.some(link => link?.account_id)));
}

export const NO_ACCOUNT_FILTER = 'no_account';

export function filterAccounts(accounts, search, statusFilter) {
    const q = search.trim().toLocaleLowerCase();
    return accounts.filter(a => {
        if (a.status === 'merged' || a.merged_into_id) return false;
        if (statusFilter === 'all' && a.status === 'archived') return false;
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        if (!q) return true;
        if (contactMatchesCrmSearch(a.primary_contact, q)) return true;
        return [a.account_name, a.account_number, a.tier?.name]
            .some(v => String(v || '').toLocaleLowerCase().includes(q));
    });
}

function ClientsView({ userId, isUser = false } = {}) {
    const { allowed: crmEditAllowed } = useModuleAccess(isUser ? 'crm' : null, 'edit');
    const { allowed: financialViewAllowed } = useModuleAccess(isUser ? 'financial' : null, 'view');
    const canEditCrm = !isUser || crmEditAllowed;
    const canViewFinancial = !isUser || financialViewAllowed;

    const [accounts, setAccounts] = useState([]);
    const [entities, setEntities] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    // Comms next pass (5d): contacts left with no account (e.g. their account was permanently deleted) stay visible here.
    const [orphanContacts, setOrphanContacts] = useState(null);
    const [orphanError, setOrphanError] = useState(null);
    const loadOrphanContacts = useCallback(async () => {
        setOrphanError(null);
        const { data, error } = await supabaseClient
            .from('contacts')
            .select('id, org_id, name, position, emails, phones, tags, primary_account_id, merged_into_id, contact_methods ( id, kind, label, value, is_primary ), contact_accounts!contact_accounts_contact_id_fkey ( account_id )')
            .is('primary_account_id', null)
            .is('merged_into_id', null)
            .order('name', { ascending: true });
        if (error) {
            setOrphanError(error.message || 'Could not load contacts without an account.');
            setOrphanContacts([]);
            return;
        }
        setOrphanContacts(contactsWithoutAccount(data || []));
    }, []);
    useEffect(() => {
        if (statusFilter === NO_ACCOUNT_FILTER) loadOrphanContacts();
    }, [statusFilter, loadOrphanContacts]);
    const visibleOrphans = useMemo(
        () => (orphanContacts || []).filter(c => contactMatchesCrmSearch(c, search)),
        [orphanContacts, search]);

    const [viewMode, setViewMode] = useViewModePreference({
        userId,
        storageKey: VIEW_STORAGE_KEY,
        modes: VIEW_MODES,
        defaultMode: 'List',
    });
    // True once we've reconciled the localStorage guess above against this user's saved
    // preference row (or established there isn't one) — gates the write-back effect below
    // so a fresh tab doesn't race a stale default over a value Supabase already remembers.
    // A ref (not state) on purpose: flipping it must not, by itself, re-run the write-back
    // effect below when the view mode never actually changed.
    // Set right before the load effect below applies a value it just fetched, so the
    // write-back effect skips echoing that same value straight back to Supabase.

    const [showAdd, setShowAdd] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [duplicates, setDuplicates] = useState(null);

    // BL-177 PR D: CRM tabs Company | My Contacts | Shared with me. My Contacts and sharing are switched on/off by the platform (and a company
    // may only be stricter); the tabs and the "My Contacts" Save-to choice simply do not exist when the database says off.
    const [personalStatus, setPersonalStatus] = useState({ enabled: false, sharing_enabled: false });
    const [tab, setTab] = useState('company');
    const [personalVersion, setPersonalVersion] = useState(0);
    const [addTarget, setAddTarget] = useState('company');
    useEffect(() => {
        let cancelled = false;
        fetchPersonalContactsStatus(supabaseClient).then((status) => { if (!cancelled) setPersonalStatus(status); });
        return () => { cancelled = true; };
    }, []);
    const tabs = CRM_TABS.filter(t => t.key === 'company' || (t.key === 'mine' && personalStatus.enabled) || (t.key === 'shared' && personalStatus.sharing_enabled));
    const activeTab = tabs.some(t => t.key === tab) ? tab : 'company';

    // EDIT / DELETE / MERGE / IMPORT / EXPORT are for company admins (and holders of CRM "manage"); the database enforces it.
    // Everyone with access can view and ADD.
    const manageable = useCanManageCompanyContacts(useMemo(
        () => [...entities.map(e => e.id), ...accounts.map(a => a.entity_id)], [entities, accounts]));
    const manageableEntities = entities.filter(e => manageable.has(e.id));
    const canManageAny = manageable.size > 0;

    // ---- Data loading -------------------------------------------------------
    const loadAccounts = useCallback(async () => {
        setLoadError(null);
        const { data: accountRows, error: accountError } = await supabaseClient
            .from('accounts')
            .select(ACCOUNT_SELECT)
            .order('created_at', { ascending: false });
        if (accountError) {
            setLoadError(accountError.message);
            setAccounts([]);
        } else {
            const accountIds = (accountRows || []).map(account => account.id).filter(Boolean);
            let invoiceRows = [];

            if (accountIds.length && canViewFinancial) {
                const batches = [];
                for (let index = 0; index < accountIds.length; index += INVOICE_ACTIVITY_BATCH_SIZE) {
                    batches.push(accountIds.slice(index, index + INVOICE_ACTIVITY_BATCH_SIZE));
                }

                const results = await Promise.all(batches.map(ids => (
                    supabaseClient
                        .from('invoices')
                        .select('account_id, invoice_number, status, lifecycle_status, total, final_amount, created_at, updated_at, due_date, payment_date')
                        .in('account_id', ids)
                        .order('created_at', { ascending: false })
                )));

                const invoiceError = results.find(result => result.error)?.error;
                if (invoiceError) {
                    setLoadError(`Clients loaded, but recent invoice activity could not be loaded: ${invoiceError.message}`);
                }
                invoiceRows = results.flatMap(result => result.data || []);
            }

            const invoicesByAccount = new Map();
            invoiceRows.forEach((invoice) => {
                if (!invoicesByAccount.has(invoice.account_id)) {
                    invoicesByAccount.set(invoice.account_id, []);
                }
                invoicesByAccount.get(invoice.account_id).push(invoice);
            });

            setAccounts((accountRows || []).map(account => ({
                ...account,
                invoices: invoicesByAccount.get(account.id) || [],
            })));
        }
        setLoading(false);
    }, [canViewFinancial]);

    // Accounts must hang off a booking node — that is where the account number's prefix
    // comes from (<entity_code>-NNNNN), so an entity without a code cannot number a client.
    const loadEntities = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('entities')
            .select('id, entity_code, name')
            .eq('is_booking_node', true)
            .order('entity_code');
        if (!error) setEntities(data || []);
    }, []);

    // Tier profiles are tenant-wide config (shared, not private-book) — the Tier-3 edit
    // dropdown needs the ladder, so load it once here rather than per card.
    const loadTiers = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('tier_profiles')
            .select('id, name, rank')
            .order('rank');
        if (!error) setTiers(data || []);
    }, []);

    const loadDrivers = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('drivers')
            .select('id, status, employment_classification')
            .order('created_at');
        if (!error) setDrivers(data || []);
    }, []);

    useEffect(() => {
        loadAccounts();
        loadEntities();
        loadTiers();
        loadDrivers();
    }, [loadAccounts, loadEntities, loadTiers, loadDrivers]);

    // ---- Create -------------------------------------------------------------
    // Anyone with access can add. A duplicate check (any matching E.164 phone, then any matching email) runs first; a match is shown and
    // the person decides. The database links the new person to the account and makes them its primary contact.
    const createClient = async (form, { allowDuplicate = false } = {}) => {
        setSaving(true);
        setSaveError(null);
        try {
            // BL-177 Step 2m: no communication preferences are sent. They belong to the client (the new contact starts on the standard
            // defaults and the client sets their own in the client app).
            const outcome = await addCompanyClient(supabaseClient, { form, allowDuplicate });
            if (outcome.duplicates) {
                setDuplicates({ form, matches: outcome.duplicates });
                return;
            }
            setDuplicates(null);
            await loadAccounts();
            setShowAdd(false);
        } catch (err) {
            setSaveError(err.message || String(err));
        } finally {
            setSaving(false);
        }
    };

    // Save to "My Contacts": private to the signed-in person (the database makes it so; nobody else, admins included, can read it).
    const createPersonal = async (draft) => {
        setSaving(true);
        setSaveError(null);
        try {
            await createPersonalContact(supabaseClient, { form: draft, ownerId: userId });
            setPersonalVersion(v => v + 1);
            setShowAdd(false);
            setTab('mine');
        } catch (err) {
            setSaveError(err.message || String(err));
        } finally {
            setSaving(false);
        }
    };

    // Export: a CSV of what is on screen right now (search and status filter applied), logged with who, when and how many rows.
    const exportContacts = async () => {
        const rowsByEntity = new Map();
        filtered.forEach(a => rowsByEntity.set(a.entity_id, (rowsByEntity.get(a.entity_id) || 0) + 1));
        const allowedRows = filtered.filter(a => manageable.has(a.entity_id));
        try {
            for (const [entityId, count] of rowsByEntity) {
                if (!manageable.has(entityId)) continue;
                const { error } = await supabaseClient.rpc('crm_log_contact_export', {
                    p_entity: entityId, p_row_count: count, p_filters: { search, status: statusFilter },
                });
                if (error) throw error;
            }
            downloadCsv(`company-contacts-${new Date().toISOString().slice(0, 10)}.csv`, buildExportCsv(allowedRows));
        } catch (err) {
            setLoadError(`Export failed: ${err.message || err}`);
        }
    };

    // ---- Filtering ----------------------------------------------------------
    const filtered = useMemo(
        () => filterAccounts(accounts, search, statusFilter),
        [accounts, search, statusFilter]
    );

    // ---- Current activity (Hybrid view only; every account in one call, never one per card) ----------------
    const activityIds = useMemo(
        () => (viewMode === 'Hybrid' && activeTab === 'company' ? accounts.filter(a => a.status !== 'merged' && !a.merged_into_id).map(a => a.id) : []),
        [accounts, viewMode, activeTab]);
    const { activity: currentActivity, loading: activityLoading } = useCurrentActivity(activityIds, { enabled: activityIds.length > 0 });

    // ---- Chrome -------------------------------------------------------------
    const canvas = 'text-[var(--ink)]';
    const panel = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const subText = 'text-[var(--ink-muted)]';

    return (
        <div data-m6-layout="clients" data-m6-component="ClientsView">
            <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold ${canvas}`}>Clients</h2>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {canManageAny && activeTab === 'company' && (
                        <>
                            <button type="button" onClick={() => setShowImport(v => !v)} aria-pressed={showImport}
                                className={standardPillClass(showImport)}>
                                Import
                            </button>
                            <button type="button" onClick={exportContacts} disabled={filtered.length === 0}
                                className={BTN}>
                                Export
                            </button>
                        </>
                    )}
                    {(canEditCrm || personalStatus.enabled) && <SupportWriteGuard>
                        <button
                            onClick={() => { setShowAdd(v => !v); setSaveError(null); setDuplicates(null); setAddTarget(canEditCrm ? 'company' : 'mine'); }}
                            className={BTN}
                        >
                            {showAdd ? 'Cancel' : '+ Add Client'}
                        </button>
                    </SupportWriteGuard>}
                </div>
            </div>

            {tabs.length > 1 && (
                <div role="tablist" aria-label="Contacts" className="mb-3 flex flex-wrap gap-2" data-testid="crm-tabs">
                    {tabs.map(t => (
                        <button key={t.key} type="button" role="tab" aria-selected={activeTab === t.key} onClick={() => setTab(t.key)}
                            className={standardPillClass(activeTab === t.key)}>
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Toolbar: search · status · view switch */}
            <div className={`${TILE} p-3 pt-4 mb-4 flex flex-wrap gap-3 items-center`}>
                <div className="flex flex-1 min-w-[200px] items-center">
                    <input
                        type="text"
                        placeholder={activeTab === 'company' ? 'Search clients…' : 'Search contacts…'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className={`flex-1 px-3 py-2 rounded-lg border focus:ring-2 focus:ring-brand-500 outline-none
                                    bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)] placeholder-[var(--ink-muted)]`}
                    />
                    <HoverHelp helpId="crm.clients.search" userId={userId} />
                </div>
                {activeTab === 'company' && <div className="flex items-center">
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className={`px-3 py-2 rounded-lg border focus:ring-2 focus:ring-brand-500 outline-none
                                    bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]`}
                    >
                        <option value="all">All statuses</option>
                        {/* Canonical value under the hood, the tenant's word on screen. */}
                        {Object.entries(ACCOUNT_STATUS_META).map(([value, m]) => (
                            <option key={value} value={value}>{m.label}</option>
                        ))}
                        <option value={NO_ACCOUNT_FILTER}>Contacts without an account</option>
                    </select>
                    <HoverHelp helpId="crm.clients.status-filter" userId={userId} />
                </div>}

                <div className="inline-flex items-center gap-2" role="group" aria-label="Client view">
                    {VIEW_MODES.map(mode => (
                        <button
                            key={mode}
                            type="button"
                            aria-pressed={viewMode === mode}
                            onClick={() => setViewMode(mode)}
                            className={standardPillClass(viewMode === mode)}
                        >
                            {mode}
                        </button>
                    ))}
                </div>
                <HoverHelp helpId="crm.clients.view-mode" userId={userId} />
            </div>

            {showImport && canManageAny && activeTab === 'company' && (
                <ContactImportPanel entities={manageableEntities} onClose={() => setShowImport(false)} onDone={loadAccounts} />
            )}

            {showAdd && (
                <AddClientPanel
                    key={addTarget}
                    personalEnabled={personalStatus.enabled}
                    companyAllowed={canEditCrm}
                    initialSaveTo={addTarget}
                    onSubmitPersonal={createPersonal}
                    entities={entities}
                    saving={saving}
                    error={saveError}
                    duplicates={duplicates}
                    onSubmit={createClient}
                    onAddAnyway={() => duplicates && createClient(duplicates.form, { allowDuplicate: true })}
                    onDismissDuplicates={() => setDuplicates(null)}
                    userId={userId}
                />
            )}

            {loadError && (
                <div className="mb-4 p-3 rounded-lg border bg-[var(--ui-alert)]/10 text-sm" style={toneTextStyle('alert')}>
                    Couldn’t load clients: {loadError}
                </div>
            )}

            {activeTab !== 'company' ? (
                <PersonalContactsPanel
                    tab={activeTab}
                    userId={userId}
                    status={personalStatus}
                    viewMode={viewMode}
                    search={search}
                    entities={entities}
                    version={personalVersion}
                    onAdd={() => { setAddTarget('mine'); setShowAdd(true); setSaveError(null); setDuplicates(null); }}
                />
            ) : statusFilter === NO_ACCOUNT_FILTER ? (
                <section className={`${TILE} ia-tile3d--clip pt-[5px]`} aria-label="Contacts without an account" data-orphan-contacts="true">
                    <div className="px-4 py-3 border-b border-[var(--surface-border)]">
                        <h2 className={`font-semibold ${canvas}`}>Contacts without an account</h2>
                        <p className={`text-sm mt-1 ${subText}`}>
                            These people are still in the CRM, but no account is linked to them — usually because their account was deleted.
                        </p>
                    </div>
                    {orphanError && (
                        <p role="alert" className="px-4 py-3 text-sm" style={toneTextStyle('alert')}>{orphanError}</p>
                    )}
                    {orphanContacts === null ? (
                        <div className={`p-8 text-center ${subText}`}>Loading…</div>
                    ) : visibleOrphans.length === 0 ? (
                        <div className={`p-8 text-center ${subText}`}>
                            {orphanContacts.length === 0 ? 'Every contact has an account.' : 'Nothing matches that search'}
                        </div>
                    ) : (
                        <ul className="divide-y divide-[var(--surface-border)]">
                            {visibleOrphans.map(c => {
                                const email = contactEmailValues(c)[0] || '';
                                const phone = (c.phones || [])[0] || '';
                                return (
                                    <li key={c.id} className="px-4 py-3" data-orphan-contact={c.id}>
                                        <div className={`font-medium ${canvas}`}>{c.name}</div>
                                        <div className={`text-sm ${subText}`}>
                                            {[c.position, email, phone ? formatPhone(phone) : ''].filter(Boolean).join(' · ') || 'No email or phone on file'}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            ) : loading ? (
                <div className={`p-8 text-center ${subText}`}>Loading…</div>
            ) : filtered.length === 0 ? (
                <div className={`${TILE} p-10 text-center`}>
                    <div className={`font-medium ${canvas}`}>
                        {accounts.length === 0 ? 'No clients yet' : 'Nothing matches that search'}
                    </div>
                    <div className={`text-sm mt-1 ${subText}`}>
                        {accounts.length === 0
                            ? 'Add your first client — it starts as a Lead and gets its account number when it converts.'
                            : 'Try a different search or status.'}
                    </div>
                </div>
            ) : viewMode === 'List' ? (
                <div className={`${TILE} ia-tile3d--clip pt-[5px]`}>
                    <div className="overflow-x-auto">
                        <ResponsiveTable className="w-full">
                            <thead className="bg-[var(--surface-sunken)]">
                                <tr>
                                    {COLUMNS.map(c => (
                                        <th key={c} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">
                                            {c}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--surface-border)]">
                                {filtered.map(a => (
                                    <ClientDisclosure
                                        key={a.id}
                                        account={a}
                                        tiers={tiers}
                                        drivers={drivers}
                                        mode="List"
                                        onSaved={loadAccounts}
                                        canEdit={canEditCrm && manageable.has(a.entity_id)}
                                        canViewFinancial={canViewFinancial}
                                    />
                                ))}
                            </tbody>
                        </ResponsiveTable>
                    </div>
                </div>
            ) : viewMode === 'Hybrid' ? (
                // Hybrid: the Tier-2 card IS the resting state — and it can still step down
                // to Tier 3 in place, same as from any other door. Exactly two cards per row from the sm
                // breakpoint up (one on a phone); each card is two halves (the card + Current activity).
                <div className="grid grid-flow-row-dense grid-cols-1 gap-3 sm:grid-cols-2" data-testid="hybrid-grid">
                    {filtered.map(a => (
                        <ClientDisclosure
                            key={a.id}
                            account={a}
                            tiers={tiers}
                            drivers={drivers}
                            mode="Hybrid"
                            activity={currentActivity[a.id] || EMPTY_ACTIVITY}
                            activityLoading={activityLoading}
                            onSaved={loadAccounts}
                            canEdit={canEditCrm && manageable.has(a.entity_id)}
                            canViewFinancial={canViewFinancial}
                        />
                    ))}
                </div>
            ) : (
                // Card: a grid of tiles; clicking one opens the same card in place.
                // Comms next pass (5c): an open card spans the whole row; "dense" lets the next tiles fill the cell it left
                // behind (that hole was the large blank gap in a narrow window), and closing returns to the plain order.
                <div className="grid grid-flow-row-dense gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="card-grid">
                    {filtered.map(a => (
                        <ClientDisclosure
                            key={a.id}
                            account={a}
                            tiers={tiers}
                            mode="Card"
                            onSaved={loadAccounts}
                            canEdit={canEditCrm && manageable.has(a.entity_id)}
                            canViewFinancial={canViewFinancial}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function revealTile(accountId) {
    const reveal = () => {
        if (typeof document === 'undefined') return;
        const tile = Array.from(document.querySelectorAll('[data-client-tile]')).find(el => el.getAttribute('data-client-tile') === String(accountId));
        if (!tile) return;
        tile.scrollIntoView?.({ block: 'nearest' });
        tile.focus?.({ preventScroll: true });
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
    else setTimeout(reveal, 0);
}

function ClientDisclosure({ account, tiers, drivers, mode, onSaved, canEdit, canViewFinancial, activity, activityLoading = false }) {
    const isList = mode === 'List';
    const initialTier = mode === 'Hybrid' ? 2 : 1;
    const saveDraft = useCallback(async (draft) => {
        await clientDisclosureConfig.saveDraft(account, draft);
        await onSaved();
    }, [account, onSaved]);

    return (
        <ProgressiveDisclosure
            record={account}
            initialTier={initialTier}
            createDraft={clientDisclosureConfig.createDraft}
            onSave={saveDraft}
            renderTier1={({ showPeek, showFull }) => {
                if (isList) {
                    return (
                        <AccountRow
                            account={account}
                            isOpen={false}
                            onToggle={showPeek}
                            colSpan={COLUMNS.length}
                        />
                    );
                }
                return <ClientTile account={account} onOpen={mode === 'Card' ? showFull : showPeek} />;
            }}
            renderTier2={({ showFull, collapse }) => {
                const card = (
                    <ClientHybridCard
                        account={account}
                        onShowMore={showFull}
                        onCollapseAll={collapse}
                        showIdentity={!isList}
                        showStrip={!isList}
                        showActivity={mode === 'Hybrid'}
                        activity={activity}
                        activityLoading={activityLoading}
                    />
                );
                if (!isList) return mode === 'Card'
                    ? <div className="sm:col-span-2 xl:col-span-3">{card}</div>
                    : card;
                return (
                    <AccountRow
                        account={account}
                        isOpen
                        onToggle={collapse}
                        onCollapseAll={collapse}
                        colSpan={COLUMNS.length}
                        detail={card}
                    />
                );
            }}
            renderTier3={(editor) => {
                // Comms next pass (5c): closing a long open card from the Card grid brings its tile back into view (and
                // focus), instead of leaving the person scrolled past the end of a now-short page looking at empty space.
                const collapseAll = mode === 'Card' ? () => {
                    editor.collapse();
                    revealTile(account.id);
                } : editor.collapse;
                const card = (
                    <ClientFullCard
                        account={account}
                        tiers={tiers}
                        drivers={drivers}
                        onCollapse={editor.showPeek}
                        onCollapseAll={collapseAll}
                        onSaved={onSaved}
                        editor={editor}
                        canEdit={canEdit}
                        canViewFinancial={canViewFinancial}
                    />
                );
                if (!isList) return mode === 'Card'
                    ? <div className="sm:col-span-2 xl:col-span-3">{card}</div>
                    : <div className="sm:col-span-2">{card}</div>;   // Hybrid: the full card spans both columns of the two-up grid
                return (
                    <AccountRow
                        account={account}
                        isOpen
                        onToggle={editor.collapse}
                        onCollapseAll={editor.collapse}
                        colSpan={COLUMNS.length}
                        detail={card}
                    />
                );
            }}
        />
    );
}

// Card view (Val, Oct 25 2026): each client is a BUSINESS CARD - landscape like a printed card (1.75 : 1), on the platform tile.
// Company across the top, the person's name and title in the middle, how to reach them along the bottom, account number in the
// corner. Activity, briefing and pipeline live in the Hybrid view, not on the card. Same target rule: the whole card opens the record.
function primaryAddress(contact) {
    const methods = (contact?.contact_methods || []).filter(m => m?.kind === 'address' && (m.value || '').trim());
    const chosen = methods.find(m => m.is_primary) || methods[0];
    return chosen ? chosen.value.trim() : null;
}

function CardIcon({ kind }) {
    const paths = {
        phone: 'M5.5 3.5h2.2l1.1 3-1.6 1.1a8.5 8.5 0 0 0 4.2 4.2l1.1-1.6 3 1.1v2.2a1.5 1.5 0 0 1-1.6 1.5C8.6 14.6 5.4 11.4 4 6.1a1.5 1.5 0 0 1 1.5-2.6z',
        email: 'M3 5.5h14v9H3zM3.5 6l6.5 5 6.5-5',
        address: 'M10 17s-5-4.6-5-8.5a5 5 0 0 1 10 0C15 12.4 10 17 10 17zM10 10.2a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4z',
    };
    return (
        <svg aria-hidden="true" viewBox="0 0 20 20" className="ia-bizcard__icon" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d={paths[kind]} />
        </svg>
    );
}

function ClientTile({ account, onOpen }) {
    const contact = account.primary_contact;
    const meta = statusMeta(account.status);
    const phone = firstPhone(contact);
    const email = firstEmail(contact);
    const address = primaryAddress(contact);
    const personName = contact?.name || account.account_name;
    // a Personal account is named after the person's last name - that is not a company, so the card says "Personal client"
    const isPersonal = account.account_kind === 'personal' || (!account.account_kind && account.is_business === false);
    const companyLine = !isPersonal && account.account_name && account.account_name !== personName ? account.account_name : (isPersonal ? 'Personal client' : 'Client');

    return (
        <button
            type="button"
            aria-expanded={false}
            onClick={() => onOpen(account.id)}
            className={`${TILE_INTERACTIVE} ia-bizcard`}
            data-testid="client-business-card"
            data-client-tile={account.id}
        >
            <div className="ia-bizcard__top">
                <div className="ia-bizcard__company">
                    <Photo contact={contact} size="sm" />
                    <span className="ia-bizcard__company-name" data-testid="bizcard-company">{companyLine}</span>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    {hasHumanReview(account) && <Pill tone="open" title="Requires human review before booking">Needs review</Pill>}
                    {account.tier && <Pill tone={tierTone(account.tier.family)}>{account.tier.name}</Pill>}
                    <Pill tone={meta.tone}>{meta.label}</Pill>
                </div>
            </div>

            <div className="min-w-0">
                <div className="ia-bizcard__name" data-testid="bizcard-name">{personName}</div>
                {contact?.position && <div className="ia-bizcard__title">{contact.position}</div>}
            </div>

            <div className="flow-rule ia-bizcard__rule" aria-hidden="true" />

            <div className="ia-bizcard__bottom">
                <div className="ia-bizcard__lines" data-testid="bizcard-contact">
                    {phone && <div className="ia-bizcard__line"><CardIcon kind="phone" /><span>{formatPhone(phone)}</span></div>}
                    {email && <div className="ia-bizcard__line"><CardIcon kind="email" /><span>{email}</span></div>}
                    {address && <div className="ia-bizcard__line"><CardIcon kind="address" /><span title={address}>{address}</span></div>}
                    {!phone && !email && !address && <div className="italic text-[var(--ink-muted)]">No phone, email or address yet</div>}
                </div>
                {account.account_number && <div className="ia-bizcard__number">{account.account_number}</div>}
            </div>
        </button>
    );
}

// Add Client — an in-place panel, not a modal (WR-013 / "in-place always").
// Account type is Business (named after the company) or Personal (named after the person's last name: a company client who happens to be
// an individual - NOT "My Contacts"). Phones / emails / addresses: one row shows, "+" adds another; each has a label and one is Primary.
function AddClientPanel({ entities, saving, error, duplicates, onSubmit, onAddAnyway, onDismissDuplicates, userId,
    personalEnabled = false, companyAllowed = true, initialSaveTo = 'company', onSubmitPersonal }) {
    // BL-177 PR D0: "Save to: Company contacts / My Contacts" (default Company contacts). Only offered while My Contacts is switched on.
    const [saveTo, setSaveTo] = useState(personalEnabled && (initialSaveTo === 'mine' || !companyAllowed) ? 'mine' : 'company');
    const [personalDraft, setPersonalDraft] = useState(blankPersonalDraft);
    const [form, setForm] = useState({
        entity_id: '', account_kind: 'business', account_name: '', contact_name: '',
        position: '',
        phones: [{ ...emptyEntry('phone'), is_primary: true }],
        emails: [{ ...emptyEntry('email'), is_primary: true }],
        addresses: [{ ...emptyEntry('address'), is_primary: true }],
        requires_human_review: false,
        time_zone: '', time_zone_source: null,   // BL-177 Step 2j: suggested from the address / phone until a person picks one
    });

    // One booking node? Choose it — don't make someone pick from a list of one.
    useEffect(() => {
        if (!form.entity_id && entities.length === 1) {
            setForm(f => ({ ...f, entity_id: entities[0].id }));
        }
    }, [entities, form.entity_id]);

    const panel = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const field = `w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-brand-500
                   bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)] placeholder-[var(--ink-muted)]`;
    const label = 'block text-xs font-semibold mb-1 text-[var(--ink-muted)]';

    const [validationError, setValidationError] = useState('');
    const validationRef = useRef(null);
    useEffect(() => { if (validationError || error) validationRef.current?.scrollIntoView?.({ block: 'nearest' }); }, [validationError, error]);
    const personal = form.account_kind === 'personal';
    // A Personal account is named from the person's first + last name; a Business one needs the typed company name.
    const ready = form.entity_id && (personal ? form.contact_name.trim() : form.account_name.trim());
    const accountNameError = validationError && form.entity_id && !personal && !form.account_name.trim() ? validationError : '';

    if (saveTo === 'mine') {
        return (
            <div className={`rounded-lg border shadow-sm p-4 mb-4 ${panel}`} data-testid="add-my-contact">
                {personalEnabled && <SaveToChoice value={saveTo} onChange={setSaveTo} companyAllowed={companyAllowed} />}
                <div className="text-sm font-semibold mb-3 text-[var(--ink)]">New contact in My Contacts — private to you. Share it later if you want to.</div>
                <PersonalContactForm draft={personalDraft} onChange={setPersonalDraft} idPrefix="new-my-contact" />
                {error && <div className="mt-3 p-2 rounded border bg-[var(--ui-alert)]/10 text-sm" style={toneTextStyle('alert')}>{error}</div>}
                <div className="mt-4 flex justify-center">
                    <button disabled={!personalDraft.name.trim() || saving} onClick={() => onSubmitPersonal?.(personalDraft)}
                        className={BTN}>
                        {saving ? 'Saving…' : 'Save to My Contacts'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={`rounded-lg border shadow-sm p-4 mb-4 ${panel}`} data-manual-contact-entry="true">
            {personalEnabled && <SaveToChoice value={saveTo} onChange={setSaveTo} companyAllowed={companyAllowed} />}
            <div className="text-sm font-semibold mb-3 text-[var(--ink)]">
                New client — starts as a Lead, and gets its account number when it converts.
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                {entities.length > 1 && (
                    <div className="sm:col-span-2">
                        <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-[var(--ink-muted)]">
                            <span>Booking node</span>
                            <HoverHelp helpId="crm.clients.booking-node" userId={userId} />
                        </div>
                        <select className={field} value={form.entity_id}
                                onChange={e => setForm({ ...form, entity_id: e.target.value })}>
                            <option value="">Choose…</option>
                            {entities.map(e => (
                                <option key={e.id} value={e.id}>{e.entity_code} — {e.name}</option>
                            ))}
                        </select>
                    </div>
                )}
                <div>
                    <label className={label} htmlFor="new-client-kind">Account type</label>
                    <select id="new-client-kind" className={field} value={form.account_kind}
                            onChange={e => { setValidationError(''); setForm(current => ({ ...current, account_kind: e.target.value })); }}>
                        <option value="business">Business — named after the company</option>
                        <option value="personal">Personal — named after the person</option>
                    </select>
                </div>
                {/* Locked hotfix: a Personal account is named after the person (first + last name), so it has no second "Last name" box. */}
                {!personal && <div>
                    <label className={label} htmlFor="new-client-name">{accountNameLabel(form.account_kind)} *</label>
                    <input id="new-client-name" className={field} value={form.account_name}
                           aria-invalid={Boolean(accountNameError)} aria-describedby={accountNameError ? 'new-client-account-error' : 'new-client-account-help'}
                           onChange={e => { setValidationError(''); setForm(current => ({ ...current, account_name: e.target.value })); }} />
                    <p id="new-client-account-help" className="mt-1 text-xs text-[var(--ink-muted)]">Type a company name. We’ll use a matching account or create one.</p>
                    {accountNameError && <p id="new-client-account-error" ref={validationRef} role="alert" className="mt-1 text-sm" style={toneTextStyle('alert')}>{accountNameError}</p>}
                </div>}
                <div>
                    <ContactNameFields value={form.contact_name} className={field} labelClassName={label} onChange={(value, parts) => setForm(current => ({ ...current, contact_name: value, contact_first_name: parts.firstName, contact_last_name: parts.lastName }))} />
                </div>
                <div>
                    <label className={label} htmlFor="new-client-position">Position</label>
                    <input id="new-client-position" className={field} value={form.position}
                           onChange={e => setForm({ ...form, position: e.target.value })} />
                </div>
                <div>
                    <label className={label}>Email</label>
                    <LabeledMultiField kind="email" ariaLabel="Email" entries={form.emails}
                        onChange={rows => setForm(f => ({ ...f, emails: rows }))} />
                </div>
                <div>
                    <label className={label}>Phone</label>
                    <LabeledMultiField kind="phone" ariaLabel="Phone" entries={form.phones}
                        onChange={rows => setForm(f => ({ ...f, phones: rows }))} />
                    <p className="mt-2 text-[11px] text-[var(--ink-muted)]" data-new-client-prefs-note="true">
                        Texts and email choices belong to the client. They set their own in the client app; until then the standard defaults apply.
                    </p>
                </div>
                <div className="sm:col-span-2">
                    <label className={label}>Address</label>
                    <LabeledMultiField kind="address" ariaLabel="Address" entries={form.addresses}
                        onChange={rows => setForm(f => ({ ...f, addresses: rows }))} />
                </div>
                <div>
                    <label className={label}>Time zone</label>
                    <ContactTimeZoneField idPrefix="new-client-time-zone" value={form.time_zone} source={form.time_zone_source}
                        addresses={form.addresses} phones={form.phones}
                        onChange={next => setForm(f => ({ ...f, ...next }))} />
                </div>
                <div className="flex flex-wrap items-center gap-4 sm:col-span-2 pt-1">
                    <label className="flex items-center gap-2 text-sm text-[var(--ink-muted)]">
                        <input type="checkbox" checked={form.requires_human_review}
                               onChange={e => setForm({ ...form, requires_human_review: e.target.checked })} />
                        <span>Requires human review (VIP ceiling)</span>
                        <HoverHelp helpId="crm.clients.human-review" userId={userId} />
                    </label>
                </div>
            </div>

            {duplicates && (
                <div role="alert" className="mt-3 rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] p-3 text-sm text-[var(--ink)]">
                    <div className="font-semibold">Someone with this phone or email is already a company contact</div>
                    <ul className="mt-1 list-disc pl-5 text-[var(--ink-muted)]">
                        {duplicates.matches.map(m => (
                            <li key={m.contact_id}>{m.name} — same {m.matches.map(x => x.on).join(' and ')}</li>
                        ))}
                    </ul>
                    <div className="mt-2 flex gap-2">
                        <button type="button" disabled={saving} onClick={onAddAnyway}
                            className={BTN}>Add anyway</button>
                        <button type="button" onClick={onDismissDuplicates}
                            className="rounded px-3 py-1 text-xs font-medium text-[var(--ink-muted)] hover:text-[var(--ink)]">Go back and edit</button>
                    </div>
                </div>
            )}

            {((validationError && !accountNameError) || error) && (
                <div ref={validationRef} role="alert"
                    className="mt-3 p-2 rounded border bg-[var(--ui-alert)]/10 text-sm"
                    style={toneTextStyle('alert')}
                >
                    {validationError || error}
                </div>
            )}

            <div className="mt-4 flex justify-center">
                <button
                    disabled={saving}
                    onClick={() => {
                        if (!ready) {
                            setValidationError(!form.entity_id ? 'Choose a booking node before saving.' : personal ? 'Enter the person’s name before saving.' : 'Enter a company name, or choose Personal for an individual contact.');
                            return;
                        }
                        setValidationError('');
                        onSubmit(form);
                    }}
                    className={BTN}
                >
                    {saving ? 'Saving…' : 'Save client'}
                </button>
            </div>
        </div>
    );
}

export default ClientsView;
